import { v4 as uuidv4 } from 'uuid';

/** First-party, bounded browser observations. Identity and attribution are resolved by the server. */
export type JourneyKind =
  | 'page'
  | 'section'
  | 'section_engaged'
  | 'section_time'
  | 'measurement'
  | 'signup_click'
  | 'checkout'
  | 'interaction'
  | 'lead'
  | 'identity';
type JourneyEvent = {
  id: string;
  at: string;
  kind: JourneyKind;
  value: string;
  option?: string;
  duration_ms?: number;
};
const storageKey = 'atrivial:journey:pending';
let pending: JourneyEvent[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;
let sending = false;
let activeRequest: Promise<void> | null = null;
let retry = 0;
let credentials: () => string | null = () => null;
let initialized = false;
let acquisition: {
  referrer: string;
  source: string | null;
  medium: string | null;
  campaign: string | null;
} | null = null;

export function journeyPage(path: string): string {
  const pathname = path.split(/[?#]/)[0];
  if (pathname === '/' || pathname === '/for-newsletters') return '/';
  if (
    /^\/(subscribe|subscription-confirmation|sign-in|get-trivia|developers|affiliates|articles|categories|portal|quiz|daily|endless-trivia)(\/|$)/.test(
      pathname
    )
  ) {
    return `/${pathname.split('/')[1]}`;
  }
  return '/other';
}
function save() {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(pending));
  } catch {
    /* Storage is optional. */
  }
}
function initialize() {
  if (initialized) return;
  initialized = true;
  const query = new URLSearchParams(location.search);
  let referrer = '';
  try {
    referrer = new URL(document.referrer).origin;
  } catch {
    /* Missing referrer stays unknown. */
  }
  acquisition = {
    referrer,
    source: query.get('utm_source'),
    medium: query.get('utm_medium'),
    campaign: query.get('utm_campaign'),
  };
  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey) ?? '[]');
    if (Array.isArray(saved))
      pending = saved
        .filter(
          (event) =>
            event && typeof event.id === 'string' && Date.parse(event.at) > Date.now() - 86400000
        )
        .slice(-96);
  } catch {
    /* Start fresh when storage is unavailable. */
  }
}
function schedule(delay = 250) {
  clearTimeout(timer);
  timer = setTimeout(() => {
    void flush();
  }, delay);
}
async function sendBatch() {
  if (sending || pending.length === 0) return false;
  sending = true;
  const batch = pending.slice(0, 32);
  const accessToken = credentials();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) headers['X-Atrivial-Visitor-Token'] = accessToken;
  let succeeded = false;
  try {
    const response = await fetch('/a/activity', {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers,
      body: JSON.stringify({
        events: batch,
        ...acquisition,
      }),
    });
    succeeded = response.ok;
    // Invalid/expired batches cannot become valid by retrying.
    if (response.status === 400 || response.status === 413) succeeded = true;
  } catch {
    /* Retry the same IDs, never manufacture another visit. */
  }
  sending = false;
  if (succeeded) {
    const ids = new Set(batch.map((event) => event.id));
    pending = pending.filter((event) => !ids.has(event.id));
    retry = 0;
    save();
    if (pending.length) schedule();
  } else if (retry < 5) {
    schedule(Math.min(1000 * 2 ** retry++, 30000));
  }
  return succeeded;
}
function flush(): Promise<void> {
  if (activeRequest) return activeRequest;
  activeRequest = (async () => {
    let succeeded = await sendBatch();
    while (succeeded && pending.length > 0 && document.visibilityState === 'hidden') {
      succeeded = await sendBatch();
    }
  })().finally(() => {
    activeRequest = null;
  });
  return activeRequest;
}
/** Let pending arrival evidence establish cookies before checkout, without blocking billing on analytics. */
export async function flushJourneyBeforeCheckout(): Promise<void> {
  if (!pending.length && !activeRequest) return;
  let deadline: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    flush(),
    new Promise<void>((resolve) => {
      deadline = setTimeout(resolve, 1500);
    }),
  ]);
  clearTimeout(deadline);
}
export function trackJourneyEvent(
  kind: JourneyKind,
  value: string,
  option?: string,
  durationMs?: number
) {
  if (
    typeof document === 'undefined' ||
    (document.visibilityState === 'hidden' && kind !== 'section_time') ||
    window.self !== window.top
  )
    return;
  let id: string;
  try {
    id = uuidv4();
  } catch {
    // Optional analytics must not interrupt the page when secure randomness is unavailable.
    return;
  }
  initialize();
  pending.push({
    id,
    at: new Date().toISOString(),
    kind,
    value,
    ...(option ? { option } : {}),
    ...(durationMs !== undefined ? { duration_ms: durationMs } : {}),
  });
  pending = pending.slice(-96);
  save();
  if (kind === 'section_time') void flush();
  else schedule();
}
export function startJourneyTracking(
  path: string,
  getAccessToken: () => string | null = () => null
) {
  credentials = getAccessToken;
  let recorded = false;
  const visible = () => {
    if (document.visibilityState !== 'hidden' && !recorded) {
      recorded = true;
      trackJourneyEvent('page', journeyPage(path));
    } else if (document.visibilityState === 'hidden') {
      void flush();
    }
  };
  let interacted = false;
  const interaction = (event: Event) => {
    if (!event.isTrusted || interacted || document.visibilityState === 'hidden') return;
    interacted = true;
    trackJourneyEvent(
      'interaction',
      event.type === 'scroll' ? 'scroll' : event.type === 'keydown' ? 'key' : 'pointer'
    );
  };
  const resume = () => {
    retry = 0;
    void flush();
  };
  const hide = () => {
    void flush();
  };
  const click = (event: MouseEvent) => {
    if (!event.isTrusted || !(event.target instanceof Element)) return;
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.getAttribute('href') ?? '', location.origin);
    if (url.origin !== location.origin) return;
    if (['/subscribe', '/get-trivia', '/sign-in'].includes(url.pathname)) {
      const plan = url.searchParams.get('plan');
      const interval = url.searchParams.get('interval');
      trackJourneyEvent(
        'signup_click',
        plan === 'individual' || plan === 'business' ? plan : url.pathname,
        interval === 'year' || interval === 'month' ? interval : undefined
      );
    }
  };
  document.addEventListener('visibilitychange', visible);
  window.addEventListener('pagehide', hide);
  window.addEventListener('online', resume);
  document.addEventListener('click', click);
  window.addEventListener('scroll', interaction, { passive: true });
  window.addEventListener('pointerdown', interaction, { passive: true });
  window.addEventListener('keydown', interaction);
  visible();
  return () => {
    document.removeEventListener('visibilitychange', visible);
    window.removeEventListener('pagehide', hide);
    window.removeEventListener('online', resume);
    document.removeEventListener('click', click);
    window.removeEventListener('scroll', interaction);
    window.removeEventListener('pointerdown', interaction);
    window.removeEventListener('keydown', interaction);
    void flush();
  };
}
